// src/components/gifts/GiftDetailModal.jsx
import { X, ShoppingCart, Zap } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Currency from "../Currency";
import { useCart } from "../../context/CartContext";
import { toast } from "react-toastify";

export default function GiftDetailModal({ gift, onClose }) {
  const navigate = useNavigate();
  const { addToCart } = useCart();

  if (!gift) return null;
  const deliversLabel =
    gift.deliversTo === "united_states"
      ? "Delivers: United States"
      : gift.deliversTo === "usa_canada_uk"
      ? "Delivers: USA, Canada, UK"
      : "Delivers: All Countries";

  const handleBuyNow = () => {
    onClose();
    navigate("/dashboard/gifts/checkout", { state: { gift } });
  };

  const handleAddToCart = () => {
    addToCart(gift, 1);
    toast.success(`${gift.title} added to cart`);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      <div className="relative w-full max-w-lg bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-h-[88vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-800 flex-shrink-0">
          <h2 className="text-sm font-bold text-gray-900 dark:text-white truncate pr-3">{gift.title}</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition flex-shrink-0">
            <X size={18} className="text-gray-500" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="relative w-full aspect-square bg-gray-100 dark:bg-gray-800">
            <img src={gift.imageUrl} alt={gift.title} className="w-full h-full object-cover" />
            {gift.isCustom && (
              <span className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-white text-gray-900 text-xs font-bold">
                CUSTOM
              </span>
            )}
          </div>

          <div className="px-4 pb-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 text-xs font-medium">
                {gift.deliveryTimeLabel}
              </span>
              <span className="px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs font-medium">
                {deliversLabel}
              </span>
            </div>

            <p className="text-xl font-bold text-purple-600 dark:text-purple-400 mb-2">
              <Currency amount={gift.priceNgn} />
            </p>

            {gift.description && (
              <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed whitespace-pre-wrap">
                {gift.description}
              </p>
            )}
            {gift.deliveryNote && (
              <p className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wide font-medium mt-2">
                {gift.deliveryNote}
              </p>
            )}
          </div>
        </div>

        <div className="px-4 py-3.5 border-t border-gray-100 dark:border-gray-800 flex gap-2 flex-shrink-0">
          <button
            onClick={handleAddToCart}
            className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-sm font-semibold text-purple-600 dark:text-purple-400 border border-purple-300 dark:border-purple-700 rounded-lg hover:bg-purple-50 dark:hover:bg-purple-900/20 transition"
          >
            <ShoppingCart size={15} /> Add to Cart
          </button>
          <button
            onClick={handleBuyNow}
            className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-sm font-semibold bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition"
          >
            <Zap size={15} /> Buy Now
          </button>
        </div>
      </div>
    </div>
  );
}